/**
 * Payroll Service - API client for payroll contracts, runs, payslips,
 * and deduction-rule endpoints
 */
import api from '../utils/api';

const payrollService = {
  // ---- Contracts ----

  listContracts: async (limit = 50, offset = 0, activeOnly = false) => {
    try {
      const params = { limit, offset };
      if (activeOnly) params.active_only = true;
      return await api.get('/api/payroll/contracts', { params });
    } catch (error) {
      console.error('Error fetching payroll contracts:', error);
      throw error;
    }
  },
  listExpiringContracts: async (days = 30) => {
    return await api.get('/api/payroll/contracts/expiring', { params: { days } });
  },
  renewContract: async (id, data = {}) => {
    try {
      return await api.post(`/api/payroll/contracts/${id}/renew`, data);
    } catch (error) {
      console.error('Error renewing payroll contract:', error);
      throw error;
    }
  },

  createContract: async (data) => {
    try {
      return await api.post('/api/payroll/contracts', data);
    } catch (error) {
      console.error('Error creating payroll contract:', error);
      throw error;
    }
  },

  importContracts: async (formData) => {
    try {
      return await api.post('/api/payroll/contracts/import', formData);
    } catch (error) {
      console.error('Error importing payroll contracts:', error);
      throw error;
    }
  },

  // ---- Payroll runs ----

  listRuns: async (limit = 50, offset = 0, status = null) => {
    try {
      const params = { limit, offset };
      if (status) params.status = status;
      return await api.get('/api/payroll/runs', { params });
    } catch (error) {
      console.error('Error fetching payroll runs:', error);
      throw error;
    }
  },

  generateRun: async (data) => {
    try {
      return await api.post('/api/payroll/runs', data);
    } catch (error) {
      console.error('Error generating payroll run:', error);
      throw error;
    }
  },

  approveRun: async (runId, notes = '') => {
    try {
      return await api.post(`/api/payroll/runs/${runId}/approve`, { notes });
    } catch (error) {
      console.error('Error approving payroll run:', error);
      throw error;
    }
  },

  rejectRun: async (runId, notes = '') => {
    try {
      return await api.post(`/api/payroll/runs/${runId}/reject`, { notes });
    } catch (error) {
      console.error('Error rejecting payroll run:', error);
      throw error;
    }
  },

  postRun: async (runId) => {
    try {
      return await api.post(`/api/payroll/runs/${runId}/post`, {});
    } catch (error) {
      console.error('Error posting payroll run:', error);
      throw error;
    }
  },

  // Actually pays staff for a posted run via Paystack Transfers — distinct
  // from postRun, which only books the GL liability. Safe to re-call: it
  // only retries line items still unpaid/failed.
  payRun: async (runId) => {
    try {
      return await api.post(`/api/payroll/runs/${runId}/pay`, {});
    } catch (error) {
      console.error('Error disbursing payroll run:', error);
      throw error;
    }
  },

  getRunLineItems: async (runId, limit = 100, offset = 0) => {
    try {
      return await api.get(`/api/payroll/runs/${runId}/lines`, { params: { limit, offset } });
    } catch (error) {
      console.error('Error fetching payroll line items:', error);
      throw error;
    }
  },

  // ---- Payslips ----

  getPayslip: async (runId, staffId) => {
    try {
      return await api.get(`/api/payroll/payslips/${runId}/${staffId}`);
    } catch (error) {
      console.error('Error fetching payslip:', error);
      throw error;
    }
  },

  // Self-service — the logged-in staff member's own payslip history.
  getMyPayslips: async () => {
    try {
      return await api.get('/api/payroll/my-payslips');
    } catch (error) {
      console.error('Error fetching my payslips:', error);
      throw error;
    }
  },

  // ---- Adjustments (bonus/penalty) ----

  listAdjustments: async (payrollRunId = null, staffId = null) => {
    try {
      const params = {};
      if (payrollRunId) params.payroll_run_id = payrollRunId;
      if (staffId) params.staff_id = staffId;
      return await api.get('/api/payroll/adjustments', { params });
    } catch (error) {
      console.error('Error fetching payroll adjustments:', error);
      throw error;
    }
  },

  createAdjustment: async (data) => {
    try {
      return await api.post('/api/payroll/adjustments', data);
    } catch (error) {
      console.error('Error creating payroll adjustment:', error);
      throw error;
    }
  },

  approveAdjustment: async (adjustmentId) => {
    try {
      return await api.post(`/api/payroll/adjustments/${adjustmentId}/approve`, {});
    } catch (error) {
      console.error('Error approving payroll adjustment:', error);
      throw error;
    }
  },

  deleteAdjustment: async (adjustmentId) => {
    try {
      return await api.delete(`/api/payroll/adjustments/${adjustmentId}`);
    } catch (error) {
      console.error('Error deleting payroll adjustment:', error);
      throw error;
    }
  },

  // ---- Deduction rules ----

  listRules: async () => {
    try {
      return await api.get('/api/payroll/rules');
    } catch (error) {
      console.error('Error fetching deduction rules:', error);
      throw error;
    }
  },

  getRulePresets: async () => {
    try {
      return await api.get('/api/payroll/rules/presets/templates');
    } catch (error) {
      console.error('Error fetching deduction rule presets:', error);
      throw error;
    }
  },

  createRule: async (data) => {
    try {
      return await api.post('/api/payroll/rules', data);
    } catch (error) {
      console.error('Error creating deduction rule:', error);
      throw error;
    }
  },

  deleteRule: async (ruleId) => {
    try {
      return await api.delete(`/api/payroll/rules/${ruleId}`);
    } catch (error) {
      console.error('Error deleting deduction rule:', error);
      throw error;
    }
  },

  applyRulePresets: async (presetNames) => {
    try {
      return await api.post('/api/payroll/rules/presets/apply', presetNames);
    } catch (error) {
      console.error('Error applying deduction rule presets:', error);
      throw error;
    }
  },

  testRule: async (ruleId, staffId, basicSalary) => {
    try {
      return await api.post('/api/payroll/rules/test', null, {
        params: { rule_id: ruleId, staff_id: staffId, basic_salary: basicSalary }
      });
    } catch (error) {
      console.error('Error testing deduction rule:', error);
      throw error;
    }
  },

  // ---- Payslip / statutory downloads ----
  // Both use { raw: true } so utils/api.js returns the raw fetch Response
  // instead of JSON-parsing it — caller does response.blob() and triggers
  // the download. See ReportCardPreviewModal.js for the consumer pattern.

  getPayslipPdf: async (runId, staffId) => {
    try {
      return await api.get(`/api/payroll/payslips/${runId}/${staffId}/pdf`, { raw: true });
    } catch (error) {
      console.error('Error downloading payslip PDF:', error);
      throw error;
    }
  },

  getSsnitExport: async (runId) => {
    try {
      return await api.get(`/api/payroll/runs/${runId}/ssnit-export`, { raw: true });
    } catch (error) {
      console.error('Error downloading SSNIT export:', error);
      throw error;
    }
  },

  getBankPaymentExport: async (runId) => {
    try {
      return await api.get(`/api/payroll/runs/${runId}/bank-payment-export`, { raw: true });
    } catch (error) {
      console.error('Error downloading bank payment export:', error);
      throw error;
    }
  },

  getAnnualSummary: async (year) => {
    try {
      return await api.get('/api/payroll/annual-summary', { params: { year }, raw: true });
    } catch (error) {
      console.error('Error downloading annual summary:', error);
      throw error;
    }
  },

  // ---- Staff loans / advances ----

  createLoan: async (data) => {
    try {
      return await api.post('/api/payroll/loans', data);
    } catch (error) {
      console.error('Error creating staff loan:', error);
      throw error;
    }
  },

  listLoans: async (staffId = null, status = null) => {
    try {
      const params = {};
      if (staffId) params.staff_id = staffId;
      if (status) params.status = status;
      return await api.get('/api/payroll/loans', { params });
    } catch (error) {
      console.error('Error fetching staff loans:', error);
      throw error;
    }
  },

  getLoan: async (loanId) => {
    try {
      return await api.get(`/api/payroll/loans/${loanId}`);
    } catch (error) {
      console.error('Error fetching staff loan:', error);
      throw error;
    }
  },

  approveLoan: async (loanId) => {
    try {
      return await api.post(`/api/payroll/loans/${loanId}/approve`, {});
    } catch (error) {
      console.error('Error approving staff loan:', error);
      throw error;
    }
  },

  rejectLoan: async (loanId) => {
    try {
      return await api.post(`/api/payroll/loans/${loanId}/reject`, {});
    } catch (error) {
      console.error('Error rejecting staff loan:', error);
      throw error;
    }
  },

  listLoanRepayments: async (loanId) => {
    try {
      return await api.get(`/api/payroll/loans/${loanId}/repayments`);
    } catch (error) {
      console.error('Error fetching loan repayments:', error);
      throw error;
    }
  },

  // ---- Leave encashment ----

  createLeaveEncashment: async (data) => {
    try {
      return await api.post('/api/payroll/leave-encashments', data);
    } catch (error) {
      console.error('Error creating leave encashment request:', error);
      throw error;
    }
  },

  listLeaveEncashments: async (staffId = null, status = null) => {
    try {
      const params = {};
      if (staffId) params.staff_id = staffId;
      if (status) params.status = status;
      return await api.get('/api/payroll/leave-encashments', { params });
    } catch (error) {
      console.error('Error fetching leave encashment requests:', error);
      throw error;
    }
  },

  getLeaveEncashment: async (requestId) => {
    try {
      return await api.get(`/api/payroll/leave-encashments/${requestId}`);
    } catch (error) {
      console.error('Error fetching leave encashment request:', error);
      throw error;
    }
  },

  approveLeaveEncashment: async (requestId) => {
    try {
      return await api.post(`/api/payroll/leave-encashments/${requestId}/approve`, {});
    } catch (error) {
      console.error('Error approving leave encashment request:', error);
      throw error;
    }
  },

  rejectLeaveEncashment: async (requestId) => {
    try {
      return await api.post(`/api/payroll/leave-encashments/${requestId}/reject`, {});
    } catch (error) {
      console.error('Error rejecting leave encashment request:', error);
      throw error;
    }
  }
};

export default payrollService;
